import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { catchError, firstValueFrom, of } from 'rxjs';

import { isBackendErrorEvent } from '../../../../backend/BackendErrorEvent';
import { ValidationService } from '../../../utils/valibot/validation.service';
import { VehicleSearchQuery } from '../../model/customer-search-query.model';
import { EsoCrmCustomerDataResponse, EsoCrmCustomerDataResponseSchema } from './model/response.model';

// The error types returned from the ESO CRM API, with the prefix 'ESO_CRM_'.
enum EsoCrmErrorType {
  ESO_CRM_VIN_OR_LICENSEPLATE_NOT_FOUND = 'ESO_CRM_VIN_OR_LICENSEPLATE_NOT_FOUND',
}

/**
 * For external APIs like this we decided to only soft-validate responses using Valibot schemas.
 */
@Injectable({
  providedIn: 'root',
})
export class EsoCrmClient {
  private static readonly urlPrefix = `eso/crm`;

  readonly http = inject(HttpClient);
  readonly logger = inject(NGXLogger);
  private readonly validationService = inject(ValidationService);

  searchCustomersViaVehicle(query: VehicleSearchQuery): Promise<EsoCrmCustomerDataResponse> {
    let queryParams = new HttpParams();
    if (query.vin) {
      queryParams = queryParams.set('vin', query.vin);
    }
    if (query.licenceNumber) {
      queryParams = queryParams.set('licensePlate', query.licenceNumber);
    }

    const url = `${EsoCrmClient.urlPrefix}/okus`;
    this.logger.info(`#CDFTF0120 searching for customers via vehicle ${url}`);
    return firstValueFrom(
      this.http.get(url, { params: queryParams }).pipe(
        this.validationService.pipeResponseSoftFail(EsoCrmCustomerDataResponseSchema),
        catchError((error: HttpErrorResponse) => {
          // Handle 404 with type ESO_CRM_VIN_OR_LICENSEPLATE_NOT_FOUND as zero results
          if (this.errorHasType(error.error, EsoCrmErrorType.ESO_CRM_VIN_OR_LICENSEPLATE_NOT_FOUND)) {
            return of([]);
          }

          this.logger.error('#CDFTF0123 Could not search for customers via vehicle search', error);
          throw error;
        }),
      ),
    );
  }

  private errorHasType(error: unknown, type: EsoCrmErrorType): boolean {
    return isBackendErrorEvent(error) && error.type === type;
  }
}
